import React from 'react';
import { fmt, bandColor } from '../../lib/format.js';

// KpiCard {label,value,unit?,sub?,band?,hero?,icon?} — §12
export function KpiCard({ label, value, unit, sub, band, hero, icon, testid }) {
  return (
    <div className={`kpi-card ${hero ? 'hero' : ''} fade-up`} data-testid={testid}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">
        {value}
        {unit && <span style={{ fontSize: 14, marginLeft: 4, fontWeight: 600, opacity: .75 }}>{unit}</span>}
      </div>
      {band && <BandPill band={band} />}
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}

export function BandPill({ band }) {
  if (!band) return <span style={{ color: 'var(--ink-4)' }}>—</span>;
  const c = bandColor(band);
  return (
    <span
      className="pill"
      style={{ background: `${c}20`, color: c, borderColor: `${c}33` }}
      data-testid={`band-${band}`}
    >
      <span className="dot" />
      {band}
    </span>
  );
}

export function ToolTile({ label, score, band, projects, color, testid }) {
  const pct = score ? Math.min(100, (score / 5) * 100) : 0;
  return (
    <div className="card fade-up" style={{ padding: 16, position: 'relative', overflow: 'hidden' }} data-testid={testid}>
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: color,
      }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
        <div>
          <div className="section-title" style={{ margin: 0 }}>{label}</div>
          <div style={{ fontSize: 24, fontWeight: 800 }} className="tabular">
            {fmt(score, 2)}<span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-3)' }}> / 5</span>
          </div>
        </div>
        <BandPill band={band} />
      </div>
      <div style={{ height: 6, background: 'var(--line-2)', borderRadius: 4, overflow: 'hidden' }}>
        <div className="anim-grow" style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4 }} />
      </div>
      <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--ink-3)' }}>
        {projects} project{projects === 1 ? '' : 's'} assessed
      </div>
    </div>
  );
}

export function SectionTitle({ children, right }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
      <h3 className="section-title" style={{ margin: 0 }}>{children}</h3>
      {right}
    </div>
  );
}

export function ScoreCell({ score, testid }) {
  const b = score === null || score === undefined ? null : (score >= 4.6 ? 'Excellent' : score >= 3.6 ? 'Good' : score >= 2.6 ? 'Moderate' : 'Poor');
  if (score === null || score === undefined || Number.isNaN(score)) {
    return <span style={{ color: 'var(--ink-4)' }} data-testid={testid}>—</span>;
  }
  const c = bandColor(b);
  return (
    <span className="score-cell" style={{ background: `${c}20`, color: c }} data-testid={testid}>
      {fmt(score, 2)}
    </span>
  );
}

// DataBarCell — value with faint band-coloured bar behind
export function DataBarCell({ value, max = 5, colorHint, testid }) {
  const v = value === null || value === undefined || Number.isNaN(value) ? null : value;
  const pct = v !== null ? Math.max(0, Math.min(100, (v / max) * 100)) : 0;
  const b = v === null ? null : (v >= 4.6 ? 'Excellent' : v >= 3.6 ? 'Good' : v >= 2.6 ? 'Moderate' : 'Poor');
  const c = colorHint || bandColor(b);
  return (
    <div style={{ position: 'relative', minWidth: 90 }} data-testid={testid}>
      <div style={{
        position: 'absolute', inset: 0, borderRadius: 4, background: `${c}15`,
      }} />
      <div className="anim-grow" style={{
        position: 'absolute', top: 0, bottom: 0, left: 0, width: `${pct}%`,
        borderRadius: 4, background: `${c}45`,
      }} />
      <div style={{ position: 'relative', padding: '3px 8px', fontWeight: 700, color: c }} className="tabular">
        {v === null ? '—' : fmt(v, 2)}
      </div>
    </div>
  );
}

export function SrcBanner({ source }) {
  return (
    <div className="src-banner" data-testid="src-banner">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M12 2 L14 8 L20 8 L15 12 L17 18 L12 14 L7 18 L9 12 L4 8 L10 8 Z" fill="#e23d28" />
      </svg>
      <span>Source: {source}</span>
    </div>
  );
}

export function ProvenanceFooter({ text }) {
  return (
    <div className="provenance" data-testid="provenance-footer">{text}</div>
  );
}

// Small chip
export function Chip({ children, color, testid }) {
  return (
    <span className="chip" style={color ? { background: `${color}18`, color } : undefined} data-testid={testid}>
      {children}
    </span>
  );
}

/**
 * ProvenanceBadge — compact marker showing where a card's data comes from.
 *   kind="live"   → computed from the underlying database / CSV pipeline
 *   kind="model"  → modelled placeholder pending upstream fields (Coherence, Effectiveness, Impact, Branding, submission status)
 *   kind="report" → sourced from the HDFC Bank Parivartan CSR Report FY 2024-25 (secondary source, not the OECD project cohort)
 */
export function ProvenanceBadge({ kind = 'live', note }) {
  // Neutral grey palette — provenance badges no longer compete with RAG score colours.
  // Kinds differ only by a subtle dot + label so viewers know provenance without visual noise.
  const CONFIG = {
    live:   { dot: '#3a4759', label: 'LIVE',   title: 'Live query — computed from the 186-project OECD evaluation database.' },
    model:  { dot: '#8a92a1', label: 'MODEL',  title: 'Modelled placeholder — a heuristic value pending a real feed from the CSR MIS pipeline.' },
    report: { dot: '#5b6673', label: 'REPORT', title: 'Sourced verbatim from the HDFC Bank Parivartan CSR Report FY 2024-25 (annual report, not the OECD cohort).' },
  };
  const cfg = CONFIG[kind] || CONFIG.live;
  return (
    <span
      title={note ? `${cfg.title} · ${note}` : cfg.title}
      data-testid={`provenance-${kind}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: '#f1f3f6', color: '#4a5563',
        border: '1px solid #d9dde3',
        padding: '2px 7px', borderRadius: 5,
        fontSize: 9.5, fontWeight: 700,
        letterSpacing: '.09em', textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.dot }} />
      {cfg.label}
    </span>
  );
}

// One-line legend to show inline on any page (e.g. next to the page title)
export function ProvenanceLegend() {
  return (
    <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }} data-testid="provenance-legend">
      <ProvenanceBadge kind="live" />
      <ProvenanceBadge kind="model" />
      <ProvenanceBadge kind="report" />
    </div>
  );
}
